'use client';

import { useEffect, useState } from 'react';

export function ToastOnMount() {
  const [msg, setMsg] = useState<string | null>(null);
  const [variant, setVariant] = useState<'success' | 'error'>('error');
  // Initialize from sessionStorage once on mount (consume and clear both independently)
  useEffect(() => {
    const languageMsg = sessionStorage.getItem('toast.language');
    const supportMsg = sessionStorage.getItem('toast.support');
    const languageTs = Number(sessionStorage.getItem('toast.language.ts') || '0');
    const supportTs = Number(sessionStorage.getItem('toast.support.ts') || '0');

    // Prefer the more recent one if both exist
    const next =
      languageMsg && supportMsg
        ? languageTs >= supportTs
          ? { key: 'toast.language', msg: languageMsg }
          : { key: 'toast.support', msg: supportMsg }
        : languageMsg
          ? { key: 'toast.language', msg: languageMsg }
          : supportMsg
            ? { key: 'toast.support', msg: supportMsg }
            : null;

    if (next) {
      // Use queueMicrotask to avoid synchronous setState in effect
      queueMicrotask(() => {
        setMsg(next.msg);
        // Default sessionStorage toasts to error styling for now
        setVariant('error');
      });
      sessionStorage.removeItem(next.key);
      sessionStorage.removeItem(`${next.key}.ts`);
    }
  }, []);
  // Listen for in-app toast events for immediate feedback without reload
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const ce = e as CustomEvent<
          | string
          | {
              message: string;
              key?: 'toast.language' | 'toast.support';
              variant?: 'success' | 'error';
            }
        >;
        if (typeof ce.detail === 'string') {
          setMsg(ce.detail);
          setVariant('error');
          return;
        }
        if (
          ce.detail &&
          typeof ce.detail === 'object' &&
          'message' in ce.detail &&
          typeof (ce.detail as { message?: unknown }).message === 'string'
        ) {
          const { message, key, variant } = ce.detail as {
            message: string;
            key?: 'toast.language' | 'toast.support';
            variant?: 'success' | 'error';
          };
          setMsg(message);
          setVariant(variant === 'success' ? 'success' : 'error');
          // Only clear the corresponding storage for the same key if present
          if (key) {
            sessionStorage.removeItem(key);
            sessionStorage.removeItem(`${key}.ts`);
          }
        }
      } catch {}
    };
    window.addEventListener('app:toast', handler as EventListener);
    return () => window.removeEventListener('app:toast', handler as EventListener);
  }, []);
  // Auto-hide after a short delay whenever a message is shown
  useEffect(() => {
    if (!msg) return;
    const id = setTimeout(() => setMsg(null), 3000);
    return () => clearTimeout(id);
  }, [msg]);
  if (!msg) return null;
  return (
    <div
      className={
        `fixed top-4 right-4 z-[100] text-white text-sm px-3 py-2 rounded shadow flex items-start gap-3 ` +
        (variant === 'success' ? 'bg-success-600' : 'bg-error-600')
      }
      role="status"
      aria-live="polite"
    >
      <div className="pr-6">{msg}</div>
      <button
        type="button"
        aria-label="Close"
        className="ml-auto text-white/80 hover:text-white focus:outline-none"
        onClick={() => setMsg(null)}
      >
        ×
      </button>
    </div>
  );
}
